import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { ShipmentLegRunListCard } from 'src/components/shipment/ShipmentLegRunListCard';
import LicenseContext from 'src/contexts/LicenseContext';
import { ShipmentOrderLane } from 'src/entities/ShipmentOrderLane';
import { useQueryShipmentOrderLaneDetail, useDeleteLaneManualAssignment, useResetAutoAssignments } from 'src/hooks/useShipmentOrderLanes';
import { buildShipmentLegRunItems, ShipmentLegRunItem } from 'src/utils/shipmentLegRunItems';

import { EditableAssignment, useLaneDetailEdit } from '../hooks/useLaneDetailEdit';

import LaneDetailAssignment from './LaneDetailAssignment';
import LaneDetailBasicInfo from './LaneDetailBasicInfo';
import LaneDetailBerthInfo from './LaneDetailBerthInfo';

/** タブ名 → index のマッピング (Tab の並び順) */
export const LANE_DETAIL_TAB_INDEX = {
  assignment: 0,
  berth: 1,
  legRuns: 2,
  basicInfo: 3,
} as const;
export type LaneDetailTab = keyof typeof LANE_DETAIL_TAB_INDEX;

type Props = {
  /** 対象レーンの id。`selectedLane` 指定時はそちらの id を優先できるよう optional */
  laneId?: number | null;
  /** 一覧から開いた場合の完全な Lane 情報。詳細取得中のフォールバック表示に使用 */
  selectedLane?: ShipmentOrderLane | null;
  startOn: string;
  isOpen: boolean;
  onClose: () => void;
  /** 開いた時に最初に表示するタブ。未指定時は assignment (配車管理) */
  initialTab?: LaneDetailTab;
};

/**
 * レーン詳細シートコンポーネント
 * 右側からスライドして表示
 * - V2ShipmentOrderLanes の一覧行クリックから呼ぶときは `selectedLane` を渡す
 * - それ以外のページ（配車画面等）から id 単体で開くときは `laneId` を渡す
 */
const LaneDetailSheet: FC<Props> = memo(({ laneId, selectedLane, startOn, isOpen, onClose, initialTab = 'assignment' }) => {
  const effectiveLaneId = laneId ?? selectedLane?.id ?? null;
  const initialTabIndex = LANE_DETAIL_TAB_INDEX[initialTab];
  const [tabValue, setTabValue] = useState<number>(initialTabIndex);
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDiaalogOpen, setConfirmDialogOpen] = useState(false);
  const licenseContext = useContext(LicenseContext);
  const companyId = licenseContext.config?.selected_company_id
    ?? licenseContext.config?.company_id
    ?? 0;

  useEffect(() => {
    if (isOpen) {
      setTabValue(initialTabIndex);
    }
  }, [isOpen, initialTabIndex]);

  // 詳細データ取得
  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch,
  } = useQueryShipmentOrderLaneDetail(effectiveLaneId);

  // エラー時のトースト通知
  useEffect(() => {
    if (isError && error) {
      enqueueSnackbar(
        `レーン詳細の取得に失敗しました: ${error.message || '不明なエラー'}`,
        { variant: 'error' }
      );
    }
  }, [isError, error, enqueueSnackbar]);

  // 編集状態管理
  const {
    isEditing,
    isSaving,
    requiredTruckCount,
    berthReservations,
    manualAssignments,
    modified,
    basicInfoDraft,
    setBasicInfoDraft,
    markBasicInfoDirty,
    startEditing,
    cancelEditing,
    saveChanges,
    setRequiredTruckCount,
    updateBerthReservation,
    addBerthReservation,
    removeBerthReservation,
    updateManualAssignment,
    addManualAssignment,
    removeManualAssignment,
  } = useLaneDetailEdit(detail, companyId);

  const { mutate: deleteManualAssignment } = useDeleteLaneManualAssignment();
  const { mutate: resetAutoAssignments } = useResetAutoAssignments();
  const [deleteTarget, setDeleteTarget] = useState<EditableAssignment | null>(null);

  const handleDeleteAssignment = useCallback((assignment: EditableAssignment) => {
    setDeleteTarget(assignment);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget || !detail) return;

    if (deleteTarget.isAutoAssignment && deleteTarget.legRunIds) {
      resetAutoAssignments(
        { laneId: detail.id, legRunIds: deleteTarget.legRunIds },
        {
          onSuccess: () => enqueueSnackbar('配車をリセットしました', { variant: 'success' }),
          onError: (err) => enqueueSnackbar(`リセットに失敗しました: ${err.message}`, { variant: 'error' }),
        }
      );
    } else if (deleteTarget.id) {
      deleteManualAssignment(
        { laneId: detail.id, assignmentId: deleteTarget.id },
        {
          onSuccess: () => enqueueSnackbar('配車をリセットしました', { variant: 'success' }),
          onError: (err) => enqueueSnackbar(`リセットに失敗しました: ${err.message}`, { variant: 'error' }),
        }
      );
    }
    setDeleteTarget(null);
  }, [deleteTarget, detail, deleteManualAssignment, resetAutoAssignments, enqueueSnackbar]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    if (modified) {
      setConfirmDialogOpen(true);
    } else {
      if (isEditing) {
        cancelEditing();
      }
      onClose();
    }
  };

  const handleConfirmOK = () => {
    setConfirmDialogOpen(false);
    cancelEditing();
    onClose();
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const legRunItems: ShipmentLegRunItem[] = useMemo(() => {
    if (!detail) return [];

    return detail.shipmentOrders.flatMap((shipmentData) => buildShipmentLegRunItems(shipmentData));
  }, [detail]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          minWidth: '700px',
          width: '70vw',
        },
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography variant="h4">
          レーン詳細 {effectiveLaneId != null && `#${effectiveLaneId}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {detail && !isLoading && (
            <>
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={() => {
                      void saveChanges();
                    }}
                    disabled={isSaving}
                  >
                     {isSaving ? '保存中...' : '保存'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={cancelEditing}
                    disabled={isSaving}
                  >
                    キャンセル
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={startEditing}
                >
                  編集
                </Button>
              )}
            </>
          )}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* タブ */}
      <Box sx={{ p: 2, }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="配車管理" />
          <Tab label="バース情報" />
          <Tab label="LEG一覧" />
          <Tab label="基本情報" />
        </Tabs>
      </Box>

      {/* コンテンツ */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : detail ? (
          <>
            {tabValue === 0 && (
              <LaneDetailAssignment
                detail={detail}
                isEditing={isEditing}
                requiredTruckCount={requiredTruckCount}
                manualAssignments={manualAssignments}
                fallbackAutoAssignments={selectedLane?.autoAssignments}
                onRequiredTruckCountChange={setRequiredTruckCount}
                onUpdateAssignment={updateManualAssignment}
                onAddAssignment={addManualAssignment}
                onRemoveAssignment={removeManualAssignment}
                onDeleteAssignment={handleDeleteAssignment}
                setTabValue={setTabValue}
                startOn={startOn}
              />
            )}
            {tabValue === 1 && (
              <LaneDetailBerthInfo
                detail={detail}
                isEditing={isEditing}
                berthReservations={berthReservations}
                onUpdateReservation={updateBerthReservation}
                onAddReservation={addBerthReservation}
                onRemoveBerthReservation={removeBerthReservation}
              />
            )}
            {tabValue === 2 && <ShipmentLegRunListCard rows={legRunItems} />}
            {tabValue === 3 && (
              <LaneDetailBasicInfo
                detail={detail}
                isEditing={isEditing}
                startOn={startOn}
                basicInfoDraft={basicInfoDraft}
                setBasicInfoDraft={setBasicInfoDraft}
                markBasicInfoDirty={markBasicInfoDirty}
              />
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 4,
            }}
          >
            <Typography color="text.secondary">
              データを取得できませんでした
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                void refetch();
              }}
            >
              再試行
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmDialog
        open={confirmDiaalogOpen}
        message="保存していない変更を破棄しますか？"
        handleOk={handleConfirmOK}
        handleCancel={handleConfirmCancel}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        message="この配車をリセットしますか？"
        handleOk={handleConfirmDelete}
        handleCancel={() => setDeleteTarget(null)}
      />
    </Drawer>
  );
});

export default LaneDetailSheet;
